import React, { useEffect, useState } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { Button } from "../../components/ui/button";
import { DataTable } from "../../components/DataTable";
import { AlertTriangle, Eye, LucideUserRoundCog, Trash2 } from "lucide-react";
import Modal from "../../components/Modal";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { TextArea } from "../../components/ui/TextArea";
import { requestService, requestDataMap } from "../../services";
import useUserStore from "../../stores/userStore";
import UserProfileSelect from "../../components/UserProfileSelect";
import useProfileStore from "../../stores/profileStore";
import ShortenedText from "../../components/ShortenedText";
import useUserStore2 from "../../stores/userStore2";
import MultiEmailSelect from "../../components/ChangeSelect.js";
import { toast } from "react-toastify";
import { createItemStore } from "../../stores/createItemStore";
import LoadingImage from "../../components/LoadingImage";
import { useParams } from "react-router-dom";
import StatusBadge from "../../components/table/StatusBadge"

const columns = [
  {
    accessorKey: "dateCreated",
    header: "Date",
    cell: ({ row }) => <span>{row.getValue("dateCreated").split(",")[0]}</span>,
  },
  {
    accessorKey: "requestId",
    header: "Request ID",
    cell: ({ row }) => (
      <span>
        <ShortenedText text={row.getValue("requestId")} />
      </span>
    ),
  },
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ row }) => {
      const type = row.getValue("type");
      return (
        <div className="px-4 py-2 text-sm text-[#111827]">
          <span
            className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
              type === "Invitation"
                ? "text-green-600 bg-white border border-green-600"
                : type === "Suspension"
                ? "text-[#EF4444] bg-white border border-red-500"
                : type === "Removal"
                ? "text-yellow-600 bg-white border border-yellow-500"
                : type === "Activation"
                ? "text-blue-600 bg-white border border-blue-500"
                : "text-gray-500 bg-white border border-gray-500"
            }`}
          >
            {type}
          </span>
        </div>
      );
    },
  },
  // {
  //   accessorKey: "targetProfile",
  //   header: "Target Profile",
  //   cell: ({ row }) => <span>{row.getValue("targetProfile")}</span>,
  // },
  // {
  //   accessorKey: "targetEmail", // Keep this for sorting/filtering
  //   header: "Email",
  //   cell: ({ row }) => {
  //     const targetEmail = row.getValue("targetEmail");
  //     const fallbackEmail = row.original.email; // Access the full row's `email` property
  //
  //     return <span>{targetEmail || fallbackEmail || "N/A"}</span>;
  //   },
  // },
  {
    accessorKey: "targetEmail", // Keep this for sorting/filtering
    header: "Target",
    cell: ({ row }) => {
      const targetProfile = row.original.targetProfile;
      const targetEmail = row.original.targetEmail
        ? " (" + row.original.targetEmail + ")"
        : "";
      const fallbackEmail = row.original.email;

      const target = targetProfile ? targetProfile + targetEmail : "";
      const value = target || fallbackEmail || "N/A";

      return <span>{value}</span>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return (
        <StatusBadge status={status} />
      );
    },
  },
];

// ========================================================
// Step 1: Zustand store instantiation
// ========================================================
const storeName = "requests";
const useItems = createItemStore(
  storeName,
  (request) => request.requestId,
  (args) =>
    requestService.getRequests(args[0], args[1], args[2], args[3], args[4]),
  requestDataMap
);

// ========================================================

const GroupAdminRequests = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRequestType, setSelectedRequestType] = useState();
  const [showReassignModal, setShowReassignModal] = useState();
  const { profiles = [] } = useProfileStore();
  const { groupId } = useParams();
  const { userId } = useUserStore();
  const { user } = useUserStore2((state) => state);

  // Find the membership that matches the current groupId
  const currentMembership = user?.currentProfile?.memberships?.find(
    (membership) => membership.groupId === groupId
  );

  // Get the communityId from the matching membership
  const communityId = currentMembership?.communityId;

  const handleRequestTypeChange = (type) => {
    setSelectedMember((prevState) => ({
      ...prevState,
      requestType: type,
    }));
  };

  const actions = (row) => [
    {
      label: "View Details",
      icon: <Eye />,
      onClick: () => {
        setSelectedMember(row);
        setShowViewModal(true);
      },
      variant: "dropPlain",
    },
    {
      label: "Delete Request",
      icon: <Trash2 />,
      onClick: () => {
        setSelectedMember(row);
        setShowDeleteModal(true);
      },
      variant: "dropRed",
    },
  ];

  const [reload, setReload] = useState(false);

  // ========================================================
  // Step 2: Zustand store and page hydration
  // ========================================================
  const { isLoading, fetchItems, getSortedFiltered, removeItem } = useItems(
    (state) => state
  );
  // console.log("groupId: ", groupId);
  const data = getSortedFiltered(
    (request) =>
      request.status === "Pending" &&
      request.groupID === groupId
  );
  const showLoading = data.length === 0 && isLoading;
  // console.log(`${storeName}:`, data);
  useEffect(() => {
    if (groupId) {
      // console.log("Fetching data for groupId:", groupId);
      fetchItems({}, [user?.id, undefined, undefined, undefined, groupId]);
    } else {
      console.log("groupId not available yet:", groupId);
    }
  }, [groupId, reload, user?.id, fetchItems]);
  // ========================================================

  const [targetEmail, setTargetEmail] = useState("");
  const [targetProfile, setTargetProfile] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  // Map request type to API values
  const requestTypeMapping = {
    Activation: "A",
    Invitation: "I",
    Suspension: "S",
    Removal: "R",
    "Transaction Review": "T",
    Reassign: "RA",
  };

  const displayTypeMapping = {
    A: "Activation",
    I: "Invitation",
    S: "Suspension",
    R: "Removal",
    T: "Transaction Review",
    RA: "Reassignment",
  };

  const handleSubmit = async () => {
    if (!selectedRequestType || !description) {
      alert("Please fill in all fields before submitting.");
      return;
    }

    const isInvitation = selectedRequestType === "Invitation";
    const isReassign = selectedRequestType === "Reassign";

    // Validate email for Invitation or Reassign types
    if ((isInvitation || isReassign) && !targetEmail) {
      alert("Please provide a valid email.");
      return;
    }

    // Validate profile for non-invitation/reassign types
    if (!isInvitation && !isReassign && !targetProfile) {
      alert("Please select a valid user profile.");
      return;
    }

    // Get the mapped request type code
    const requestTypeCode = requestTypeMapping[selectedRequestType] || "";

    const requestData = {
      RequestEntityId: userId,
      RequestProfileId: user.currentProfile?.id,

      CommunityId: communityId,
      Role: isReassign ? "GL" : "",
      GroupId: groupId,
      TransactionId: null,
      Type: requestTypeCode, // Use the mapped code here
      Description: description,
      Comment: "",
    };

    // Include TargetProfileId when needed (not Invitation or Reassign)
    if (!isInvitation && !isReassign) {
      (requestData.TargetProfileId = targetProfile) &&
        (requestData.TargetEntityId = targetEmail);
    }

    // If Invitation or Reassign, attach email
    if (isInvitation || isReassign) {
      requestData.Email = targetEmail;
    }

    try {
      setLoading(true);
      await requestService.addRequest(requestData);
      toast.success("🎉 Request submitted successfully!", {
        autoClose: 2000,
      });
      setShowAddModal(false);
      setShowReassignModal(false);
      setReload((prev) => !prev);
    } catch (error) {
      toast.error("❌ Failed to submit request. Please try again.", {
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedMember) return;

    setLoading(true);
    try {
      await requestService.removeRequest({
        OperatorId: userId, // Ensure this is correct
        RequestId: selectedMember.requestId,
      });

      setShowDeleteModal(false);
      removeItem(selectedMember.requestId);
      // 🔄 Trigger a reload to refetch data
      setReload((prev) => !prev);
    } catch (error) {
      console.error("Error deleting request:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTargetEmailChange = (email) => {
    setTargetEmail(Array.isArray(email) ? email[0] : email);
  };

  console.log("Target email: ", targetEmail);
  console.log("Target profile: ", targetProfile);

  return (
    <div className="flex flex-col md:flex-row min-h-screen ">
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-4 sm:p-8">
          <div className="bg-white shadow rounded-lg p-4 md:max-w-[calc(100vw-375px)] sm:p-6 text-sm mt-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 ">
              <div className="text-base sm:text-lg font-semibold text-[#111827] mb-4 sm:mb-0 ">
                Requests List
              </div>
              <div className="flex flex-col space-y-2 md820:flex-row md820:space-y-0 md820:space-x-2 ">
                <Button
                  variant={"hollowGreen"}
                  onClick={() => {
                    setSelectedRequestType("Reassign"); // This maps to "RA"
                    setShowReassignModal(true);
                  }}
                >
                  <LucideUserRoundCog />
                  Reassign Group Leader
                </Button>
                <Button onClick={() => setShowAddModal(true)}>
                  <FaPlusCircle />
                  New Request
                </Button>
              </div>
            </div>

            <hr />
            <div className="overflow-x-auto w-full max-w-screen-lg">
              {showLoading ? (
                // {getAllRequests.loading &&
                // isRequestTableEmpty(page, "group", groupId) ? (
                <LoadingImage />
              ) : data.length > 0 ? (
                // ) : !isRequestTableEmpty(page, "group", groupId) ? (
                <DataTable
                  columns={columns}
                  data={[...(data ?? [])].reverse()}
                  actions={actions}
                />
              ) : (
                <div className="text-center text-gray-500 py-6">
                  No requests found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Request */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="New Request"
        actions={
          <div className="flex flex-col sm:flex-row justify-between gap-2 w-full">
            <Button
              variant="secondary"
              onClick={() => setShowAddModal(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </div>
        }
      >
        <hr />

        {/* Request Type Buttons */}
        <Label className="mt-4 text-left">Request Type:</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {["Invitation", "Suspension", "Activation", "Removal"].map(
            (typeOption) => {
              const isSelected = selectedRequestType === typeOption;
              let buttonBackgroundColor = "";

              // Determine the background color based on the selected option
              switch (typeOption) {
                case "Invitation":
                  buttonBackgroundColor = isSelected
                    ? "bg-green-600"
                    : "bg-white";
                  break;
                case "Removal":
                  buttonBackgroundColor = isSelected
                    ? "bg-red-500"
                    : "bg-white";
                  break;
                case "Activation":
                  buttonBackgroundColor = isSelected
                    ? "bg-teal-400"
                    : "bg-white";
                  break;
                case "Suspension":
                  buttonBackgroundColor = isSelected
                    ? "bg-yellow-500"
                    : "bg-white";
                  break;
                default:
                  buttonBackgroundColor = "bg-white";
              }

              return (
                <button
                  key={typeOption}
                  onClick={() => setSelectedRequestType(typeOption)}
                  className={`flex-1 min-w-[120px] px-4 py-2 border font-semibold text-sm text-center rounded ${buttonBackgroundColor} ${
                    isSelected
                      ? "text-white border-transparent"
                      : "text-black border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {typeOption}
                </button>
              );
            }
          )}
        </div>

        {/* User Profile Select */}
        {selectedRequestType !== "Invitation" && (
          <div className="mt-4">
            <UserProfileSelect
              profiles={profiles}
              onUserSelect={setTargetEmail}
              onProfileSelect={setTargetProfile}
              groupId={groupId}
            />
          </div>
        )}

        {/* Email input for Invitation */}
        {selectedRequestType === "Invitation" && (
          <div className="mt-4">
            <Label className="text-left">Target User Email:</Label>
            <MultiEmailSelect
              selectedEmails={[targetEmail]} // Wrap in array
              onChange={handleTargetEmailChange}
              isDisabled={false}
              isMulti={false}
              className="text-left"
            />
          </div>
        )}

        {/* Description */}
        <div className="mt-4">
          <Label className="text-left">Description:</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter Description"
            className="w-full mt-1"
          />
        </div>
      </Modal>

      {/* View Modal */}
      {showViewModal && selectedMember && (
        <Modal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title="View Request Details"
          description="Below are the details of the selected request."
        >
          {/* Member ID, Date Created, Date Updated */}
          <div className="bg-[#F0FDFA] p-4 rounded-lg mb-4 grid grid-cols-2 sm:grid-cols-3 sm:grid-rows-1 gap-4">
            <div className="text-left sm:col-span-1">
              <p className="text-xs font-semibold text-gray-700">Request ID</p>
              <p className="text-base text-sm text-gray-900">
                <ShortenedText text={selectedMember.requestId} />
              </p>
            </div>
            <div className="text-left sm:col-span-1">
              <p className="text-xs font-semibold text-gray-700">
                Date Submitted
              </p>
              <p className="text-base text-sm text-gray-900">
                {selectedMember.dateCreated}
              </p>
            </div>
            <div className="text-left sm:col-span-1 sm:row-span-2">
              <p className="text-xs font-semibold text-gray-700">Status</p>
              <span
                className={`text-xs font-medium px-3 rounded-full inline-block py-1 ${
                  selectedMember.status === "Approved"
                    ? "text-green-600 bg-green-100"
                    : selectedMember.status === "Pending"
                    ? "text-yellow-600 bg-yellow-100"
                    : selectedMember.status === "Rejected"
                    ? "text-red-600 bg-red-100"
                    : "text-gray-500 bg-gray-200"
                }`}
              >
                {selectedMember.status}
              </span>
            </div>
          </div>

          {/* Status, Username, Email, Profile */}
          <div className="space-y-4">
            {/* Row 1 */}
            <div className="flex justify-start">
              <p className="text-sm font-semibold w-28 text-gray-700 text-left">
                Request Type:
              </p>
              <span
                className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                  selectedMember.type === "Invitation"
                    ? "text-green-600 bg-white border border-green-600"
                    : selectedMember.type === "Suspension"
                    ? "text-[#EF4444] bg-white border border-red-500"
                    : selectedMember.type === "Removal"
                    ? "text-yellow-600 bg-white border border-yellow-500"
                    : selectedMember.type === "Assignment"
                    ? "text-blue-600 bg-white border border-blue-500"
                    : "text-gray-500 bg-white border border-gray-500"
                }`}
              >
                {selectedMember.type || "Unknown"}
                {/*{displayTypeMapping[selectedMember.type] || "Unknown"}*/}
              </span>
            </div>

            {/* Row 2 */}

            <div className="flex justify-start text-left ">
              <p className="text-sm font-semibold w-28 text-gray-700">
                Target Account:
              </p>
              <p className="text-sm text-gray-900">
                {selectedMember.targetEntityName}
                {selectedMember.targetEmail && (
                  <>&nbsp;({selectedMember.targetEmail})</>
                )}
              </p>
            </div>

            {/* Row 3 */}
            <div className="flex justify-start text-left ">
              <p className="text-sm font-semibold w-28 text-left text-gray-700">
                Target Profile:
              </p>
              <p className="text-sm text-gray-900 text-left">
                {selectedMember.targetProfile}
              </p>
            </div>

            {/* Row 4 */}
            <div className="flex justify-start text-left">
              <p className="text-sm font-semibold w-28 text-gray-700 flex-shrink-0">
                Description:
              </p>
              <p className="text-sm text-gray-900 flex-1">
                {selectedMember.description}
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMember && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Edit Member Details"
        >
          <div className="bg-green-100 p-4 rounded-lg mb-4 grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-700">Request ID</p>
              <p className="text-base text-gray-900">
                <ShortenedText text={selectedMember.requestId} />
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">
                Date Submitted
              </p>
              <p className="text-base text-gray-900">
                {selectedMember.dateCreated}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">Status</p>
              <p className="text-base text-gray-900">{selectedMember.status}</p>
            </div>
          </div>

          <div className="text-left space-y-4">
            {[
              {
                label: "Request Type",
                value: (
                  <div className="flex space-x-2">
                    {["Activation", "Invitation", "Suspension", "Removal"].map(
                      (typeOption) => {
                        const isSelected =
                          displayTypeMapping[selectedMember.type] ===
                          typeOption;
                        const colorClasses =
                          typeOption === "Suspension"
                            ? "bg-yellow-400 text-white border-gray-300"
                            : typeOption === "Removal"
                            ? "bg-red-500 text-white border-gray-300"
                            : "bg-[#14B8A6] text-white border-[#14B8A6]";

                        return (
                          <button
                            key={typeOption}
                            onClick={() => handleRequestTypeChange(typeOption)}
                            className={`w-full px-4 py-2 border font-semibold text-sm ${
                              isSelected
                                ? colorClasses
                                : "bg-white text-black border-gray-300 hover:bg-[#F0FDFA]"
                            }`}
                          >
                            {typeOption}
                          </button>
                        );
                      }
                    )}
                  </div>
                ),
              },

              {
                label: "Target Profile",
                value: (
                  <Input
                    type="text"
                    value={selectedMember.targetProfile}
                    onChange={(e) =>
                      setSelectedMember({
                        ...selectedMember,
                        memberName: e.target.value,
                      })
                    }
                  />
                ),
              },
              {
                label: "Target Email",
                value: (
                  <Input
                    type="targetEmail"
                    value={selectedMember.targetEmail}
                    onChange={(e) =>
                      setSelectedMember({
                        ...selectedMember,
                        email: e.target.value,
                      })
                    }
                  />
                ),
              },
              {
                label: "Description",
                value: (
                  <textarea
                    className="border px-2 py-1 rounded w-full"
                    value={selectedMember.description}
                    onChange={(e) =>
                      setSelectedMember({
                        ...selectedMember,
                        description: e.target.value,
                      })
                    }
                  />
                ),
              },
            ].map((item) => (
              <div key={item.label} className="space-y-1">
                <p className="text-sm font-semibold text-gray-700">
                  {item.label}
                </p>
                <div>{item.value}</div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Reassign group leader Modal */}
      {showReassignModal && (
        <Modal
          isOpen={showReassignModal}
          onClose={() => setShowReassignModal(false)}
          title="Reassign Group Leader"
          actions={
            <div className="flex justify-between w-full">
              <Button
                variant="secondary"
                onClick={() => setShowReassignModal(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          }
        >
          {/* Request Type */}
          <Label className="mt-4 text-left">Request Type:</Label>
          <div className="flex justify-center mt-2">
            <div className="bg-sky-200 w-full font-semibold text-sky-500 text-center py-2 rounded-md">
              Reassign Group Leader
            </div>
          </div>

          {/* Name */}
          <Label className="mt-4 text-left">Name:</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter Name"
          />

          {/* Email */}
          <div className="mt-4">
            <Label className="text-left">Target User Email:</Label>
            <MultiEmailSelect
              selectedEmails={[targetEmail]}
              onChange={handleTargetEmailChange}
              isDisabled={false}
              isMulti={false}
              className="text-left"
            />
          </div>

          {/* Description */}
          <Label className="mt-4 text-left">Description:</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter Description"
            className="w-full mt-1"
          />
        </Modal>
      )}

      {/* Delete Request Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Request"
        description="Are you sure you want to delete this request? This action is NOT reversible and will result in permanent loss of this request."
        icon={<AlertTriangle />}
        iconColor="red"
        actions={
          <div className="flex flex-col sm:flex-row justify-between w-full space-y-4 sm:space-y-0 sm:space-x-2">
            <Button
              variant={"secondary"}
              onClick={() => setShowDeleteModal(false)}
              className="w-full sm:w-auto"
            >
              No, Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleConfirmDelete}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? "Deleting..." : "Yes, delete this request"}
            </Button>
          </div>
        }
      />
    </div>
  );
};

export default GroupAdminRequests;
